import React, { useEffect } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { setAuthorization, getLoggedinUser } from "../helpers/api_helper";
import { useDispatch } from "react-redux";

import { useProfile } from "../Components/Hooks/UserHooks";

import { logoutUser } from "../slices/auth/login/thunk";

const AuthProtected = (props) =>{
  const dispatch = useDispatch();
  const { userProfile, loading, token } = useProfile();
  const location = useLocation();
  
  // Check if clean=true in query parameters (require session/timestamp for bypass)
  const searchParams = new URLSearchParams(location.search);
  const isCleanMode = searchParams.get('clean') === 'true';
  const session = searchParams.get('session');
  const timestamp = searchParams.get('timestamp');
  let isSessionBypass = false;
  if (isCleanMode && session && timestamp) {
    const now = Date.now();
    let valid = false;
    try {
      const ts = BigInt(timestamp);
      const expectedSession = (ts * ts).toString();
      // Must be within 10 minutes (600000 ms)
      if (session === expectedSession && (now >= Number(ts) && now <= Number(ts) + 600000)) {
        valid = true;
      }
    } catch {}
    if (valid) {
      isSessionBypass = true;
      localStorage.setItem('sessionBypass', '1');
      localStorage.setItem('sessionBypassExpiry', (Number(timestamp) + 600000).toString());
      // Set a temporary authUser so the app sees the user as authenticated
      const tempUser = {
        email: 'bypass@guest.local',
        username: 'SessionBypass',
        token: 'bypass-' + timestamp
      };
      localStorage.setItem('authUser', JSON.stringify(tempUser));
    } else {
      // Remove any previous bypass if params are present but invalid
      localStorage.removeItem('sessionBypass');
      localStorage.removeItem('sessionBypassExpiry');
      const user = localStorage.getItem('authUser');
      if (user) {
        try {
          const parsed = JSON.parse(user);
          if (parsed && parsed.username === 'SessionBypass') {
            localStorage.removeItem('authUser');
          }
        } catch {}
      }
    }
  } else {
    // Only allow localStorage bypass if there are NO session/timestamp params in the URL
    const expiry = parseInt(localStorage.getItem('sessionBypassExpiry') || '0', 10);
    if (!session && !timestamp && localStorage.getItem('sessionBypass') === '1' && Date.now() < expiry) {
      isSessionBypass = true;
      // Ensure authUser is present for bypass
      if (!localStorage.getItem('authUser')) {
        const tempUser = {
          email: 'bypass@guest.local',
          username: 'SessionBypass',
          token: 'bypass-' + expiry
        };
        localStorage.setItem('authUser', JSON.stringify(tempUser));
      }
    } else {
      localStorage.removeItem('sessionBypass');
      localStorage.removeItem('sessionBypassExpiry');
      // Remove temp authUser if present
      const user = localStorage.getItem('authUser');
      if (user) {
        try {
          const parsed = JSON.parse(user);
          if (parsed && parsed.username === 'SessionBypass') {
            localStorage.removeItem('authUser');
          }
        } catch {}
      }
    }
  }
  
  useEffect(() => {
    if (userProfile && !loading && token) {
      setAuthorization(token);
    } else if (!userProfile && loading && !token && !isSessionBypass) {
      dispatch(logoutUser());
    }
  }, [token, userProfile, loading, dispatch, isSessionBypass]);

  /*
    Navigate is un-auth access protected routes via url
    Skip login redirect if clean=true query parameter is present (for public reports)
    */

  if (!userProfile && loading && !token && !isSessionBypass) {
    return (
      <Navigate to={{ pathname: "/login", state: { from: props.location } }} />
    );
  }

  return <>{props.children}</>;
};

const AccessRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => {
        return (<> <Component {...props} /> </>);
      }}
    />
  );
};

/**
 * Administrators only, for the template's demo pages.
 *
 * Anyone else is sent to the landing page rather than to the login screen: a
 * visitor who typed /nft-landing is not a member who lost their session, and
 * asking them to sign in to see a page that should not exist explains nothing.
 * The landing page sends a signed-in member on to their dashboard by itself,
 * so one rule covers both.
 *
 * A client-side check, which is the right weight for what it protects. These
 * pages hold no data — every one of them renders a hardcoded fixture — so the
 * risk being managed is that a member or a crawler stumbles onto a fake sign-in
 * form on our domain, and a redirect handles that. Anything reading real data
 * is guarded at the API, where it cannot be bypassed.
 */
const AdminOnly = ({ children }) => {
  const { userProfile, token } = useProfile();
  const stored = getLoggedinUser();
  const user = (userProfile && userProfile.email) ? userProfile : stored;

  // Same test the sidebar uses to decide what to render, including the legacy
  // fallbacks for sessions issued before users.is_admin existed.
  const isAdmin = !!user && (
    user.is_admin === true
    || user.is_admin === 1
    || user.email === 'houzhip@gmail.com'
    || user.role === 'admin'
    || user.username === 'admin'
  );

  if (!token || !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

export { AuthProtected, AccessRoute, AdminOnly };